import { useState } from 'react';
import { useCurrentUser } from '@/app/providers/user';
import { useLogoutRedirect } from '@/features/auth/hooks/useLogoutRedirect';
import { useNotifications } from '@/app/providers/notifications';
import { deleteAccount } from '@/features/settings/services/accountApi';
import { safeErrorMessage } from '@/shared/utils/errors';
import { Button, Card, ConfirmDialog, ErrorBanner } from '@/shared/ui';

const CONFIRM_TEXT = 'DELETE';

export default function DeleteAccountPage() {
  const { user } = useCurrentUser();
  const { pushNotification } = useNotifications();
  const doLogoutRedirect = useLogoutRedirect();

  const [pendingDelete, setPendingDelete] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleDelete = async () => {
    setPendingDelete(false);
    setIsDeleting(true);
    setError(null);
    try {
      await deleteAccount();
      pushNotification({ message: 'Your account has been deleted.' });
      // Same teardown as a deliberate sign-out -- the account's own refresh
      // token no longer exists server-side, so there is nothing for a session
      // call here to revoke, but clearing local state and landing on the
      // public page is exactly the right shape either way.
      doLogoutRedirect();
    } catch (err) {
      setIsDeleting(false);
      setError(
        safeErrorMessage(
          err,
          "We couldn't delete your account. Please try again.",
        ),
      );
    }
  };

  return (
    <section className="delete-account-page">
      {/* h2, not h1: this renders inside SettingsPage, which owns the page's h1. */}
      <div className="settings-section-head">
        <h2>Delete account</h2>
        <p className="settings-section-head__sub">
          Permanently delete your account{user ? ` (${user.email})` : ''} and everything in
          it: your videos, library, notes and saved content. This cannot be undone.
        </p>
      </div>

      {error && <ErrorBanner message={error} onDismiss={() => setError(null)} />}

      <Card className="delete-account-card">
        <p>
          Before you continue: this permanently deletes every video you've generated, your
          shelves, notes, watch history and saved items, and signs you out everywhere. If
          you have a video still generating, finish or cancel it first — deletion is
          refused while one is in progress.
        </p>
        <Button
          variant="danger"
          onClick={() => setPendingDelete(true)}
          loading={isDeleting}
        >
          {isDeleting ? 'Deleting…' : 'Delete my account'}
        </Button>
      </Card>

      <ConfirmDialog
        open={pendingDelete}
        title="Delete your account?"
        description="This permanently deletes your account and every video, note, shelf and saved item in it. This cannot be undone."
        confirmLabel="Delete my account"
        cancelLabel="Keep my account"
        confirmText={CONFIRM_TEXT}
        onConfirm={() => void handleDelete()}
        onCancel={() => setPendingDelete(false)}
      />
    </section>
  );
}
